import { useEffect, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import {
  ArrowUpRight,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  Inbox,
  Search,
  Trash2,
  X
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { buttonVariants } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import AdminChannelMessages from './AdminChannelMessages';

interface Thread {
  id: string;
  name: string;
  createdAt: string;
  userIdentifier?: string;
  userName?: string;
  userId?: string;
  tags: string[];
  metadata: {
    category?: string;
  };
}

// A thread's name is derived from its first user message and is not length-capped
// upstream, so it can be thousands of characters (someone pasted a ~10k-char
// prompt). Rendered raw it stretched the table row and grew the delete dialog past
// the viewport — and since the dialog is vertically centered, that pushed the
// Delete button off-screen, making those exact threads the ones you couldn't
// delete. Everything below shows a bounded preview; the full text is on the
// details page.
const THREAD_NAME_PREVIEW_CHARS = 180;
const PAGE_SIZES = [10, 25, 50] as const;
const DATE_PRESETS = [7, 30, 90] as const;

const previewThreadName = (name: string): string =>
  name.length > THREAD_NAME_PREVIEW_CHARS
    ? `${name.slice(0, THREAD_NAME_PREVIEW_CHARS)}…`
    : name;

const dateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const calendarDays = (month: Date): Array<Date | null> => {
  const year = month.getFullYear();
  const monthIndex = month.getMonth();
  const firstWeekday = (new Date(year, monthIndex, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
  return Array.from({ length: 42 }, (_, index) => {
    const day = index - firstWeekday + 1;
    return day > 0 && day <= daysInMonth
      ? new Date(year, monthIndex, day)
      : null;
  });
};

const AdminChatLogs = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [threads, setThreads] = useState<Thread[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [totalThreads, setTotalThreads] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [threadToDelete, setThreadToDelete] = useState<Thread | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [periodOpen, setPeriodOpen] = useState(false);
  const [visibleMonth, setVisibleMonth] = useState(() => new Date());
  const requestedPageSize = Number(searchParams.get('limit') || 25);
  const threadsPerPage = PAGE_SIZES.includes(requestedPageSize as 10 | 25 | 50)
    ? requestedPageSize
    : 25;

  // Get current page from URL params, default to 1
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  // Search and filter states
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [userSearch, setUserSearch] = useState(searchParams.get('user_search') || '');
  const [dateFrom, setDateFrom] = useState(searchParams.get('dateFrom') || '');
  const [dateTo, setDateTo] = useState(searchParams.get('dateTo') || '');
  const [tagSearch, setTagSearch] = useState(searchParams.get('tag') || '');
  const requestedTab = searchParams.get('tab');
  const activeTab = requestedTab === 'telegram' || requestedTab === 'whatsapp'
    ? requestedTab
    : 'chats';

  const changeTab = (tab: string) => {
    const params = new URLSearchParams(searchParams);
    if (tab === 'chats') params.delete('tab');
    else params.set('tab', tab);
    setSearchParams(params);
  };

  const fetchThreads = async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const requestBody: any = {
        operation: 'get_all_threads',
        page: currentPage,
        limit: threadsPerPage
      };

      // Add filters if they exist
      if (searchQuery) {
        requestBody.search = searchQuery;
      }
      if (userSearch) {
        requestBody.user_search = userSearch;
      }
      if (dateFrom) {
        requestBody.date_from = dateFrom;
      }
      if (dateTo) {
        requestBody.date_to = dateTo;
      }
      if (tagSearch) {
        requestBody.filters = [
          {
            field: 'tags',
            operator: 'in',
            value: [tagSearch]
          }
        ];
      }

      const response = await fetch('/api/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error('Failed to fetch threads');
      }

      const data = await response.json();
      setThreads(data.threads || []);
      setTotalThreads(data.total || 0);
    } catch (error) {
      console.error('Error fetching threads:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  const viewThreadDetails = (threadId: string) => {
    navigate(`/admin/chat-logs/${threadId}?returnPage=${currentPage}`);
  };

  const handleDeleteClick = (thread: Thread, e: React.MouseEvent) => {
    e.stopPropagation();
    setThreadToDelete(thread);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!threadToDelete) return;

    try {
      setDeleting(true);
      const response = await fetch('/api/threads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          operation: 'delete_thread',
          thread_id: threadToDelete.id
        })
      });

      if (!response.ok) {
        throw new Error('Failed to delete thread');
      }

      toast.success(t('admin.chatLogs.deleteSuccess', 'Thread deleted successfully'));

      // Refresh the threads list
      await fetchThreads();

      setDeleteDialogOpen(false);
      setThreadToDelete(null);
    } catch (error) {
      console.error('Error deleting thread:', error);
      toast.error(t('admin.chatLogs.deleteError', 'Failed to delete thread'));
    } finally {
      setDeleting(false);
    }
  };

  const handleApplyFilters = () => {
    const params: any = { page: '1' };
    if (searchQuery) params.search = searchQuery;
    if (userSearch) params.user_search = userSearch;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    if (tagSearch) params.tag = tagSearch;
    params.limit = String(threadsPerPage);
    setSearchParams(params);
    setPeriodOpen(false);
  };

  const handleDatePreset = (days?: number) => {
    if (!days) {
      setDateFrom('');
      setDateTo('');
      const params = new URLSearchParams(searchParams);
      params.delete('dateFrom');
      params.delete('dateTo');
      params.set('page', '1');
      setSearchParams(params);
      setPeriodOpen(false);
      return;
    }

    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days + 1);
    const nextFrom = dateInputValue(start);
    const nextTo = dateInputValue(end);
    setDateFrom(nextFrom);
    setDateTo(nextTo);
    const params = new URLSearchParams(searchParams);
    params.set('dateFrom', nextFrom);
    params.set('dateTo', nextTo);
    params.set('page', '1');
    params.set('limit', String(threadsPerPage));
    setSearchParams(params);
    setPeriodOpen(false);
  };

  const handlePageSizeChange = (value: string) => {
    const params = new URLSearchParams(searchParams);
    params.set('limit', value);
    params.set('page', '1');
    setSearchParams(params);
  };

  const handlePageChange = (page: number) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', String(page));
    setSearchParams(params);
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setUserSearch('');
    setDateFrom('');
    setDateTo('');
    setTagSearch('');
    setSearchParams({ page: '1', limit: String(threadsPerPage) });
  };

  const hasActiveFilters = searchQuery || userSearch || dateFrom || dateTo || tagSearch;
  const activeDatePreset = DATE_PRESETS.find((days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days + 1);
    return dateFrom === dateInputValue(start) && dateTo === dateInputValue(end);
  });
  const periodLabel = !dateFrom && !dateTo
    ? t('admin.chatLogs.filters.allTime', 'All time')
    : activeDatePreset
      ? t('admin.chatLogs.filters.lastDays', {
          count: activeDatePreset,
          defaultValue: `Last ${activeDatePreset} days`
        })
      : `${dateFrom || '…'} – ${dateTo || '…'}`;
  const calendarLocale = i18n.language.toLowerCase().startsWith('sr')
    ? 'sr-RS'
    : 'en-US';
  const weekdays = Array.from({ length: 7 }, (_, index) =>
    new Date(2026, 8, 7 + index).toLocaleDateString(calendarLocale, {
      weekday: 'narrow'
    })
  );

  const handleCalendarDate = (date: Date) => {
    const value = dateInputValue(date);
    if (!dateFrom || dateTo) {
      setDateFrom(value);
      setDateTo('');
      return;
    }
    if (value < dateFrom) {
      setDateFrom(value);
      setDateTo(dateFrom);
      return;
    }
    setDateTo(value);
  };

  useEffect(() => {
    fetchThreads();
  }, [currentPage, searchParams]);

  const formatDate = (dateString: string) => {
    if (!dateString) return { date: '—', time: '' };
    try {
      const value = new Date(dateString);
      const locale = i18n.language.toLowerCase().startsWith('sr')
        ? 'sr-RS'
        : 'en-GB';
      return {
        date: value.toLocaleDateString(locale, {
          timeZone: 'Europe/Belgrade',
          day: '2-digit',
          month: 'short',
          year: 'numeric'
        }),
        time: value.toLocaleTimeString(locale, {
          timeZone: 'Europe/Belgrade',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        })
      };
    } catch {
      return { date: dateString, time: '' };
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t('admin.chatLogs.title', 'Chat Logs')}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              'admin.chatLogs.description',
              'Search, review and manage conversations.'
            )}
          </p>
        </div>
        <p className="text-sm tabular-nums text-muted-foreground">
          <strong className="font-medium text-foreground">
            {totalThreads.toLocaleString()}
          </strong>{' '}
          {t('admin.chatLogs.conversations', 'conversations')}
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={changeTab}>
        <TabsList className="h-auto w-full justify-start gap-6 rounded-none border-b border-border bg-transparent p-0">
          <TabsTrigger value="chats" className="rounded-none border-b-2 border-transparent px-0 py-3 shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none">
            {t('admin.chatLogs.tabs.chats', 'Chats')}
          </TabsTrigger>
          <TabsTrigger value="telegram" className="rounded-none border-b-2 border-transparent px-0 py-3 shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none">
            Telegram
          </TabsTrigger>
          <TabsTrigger value="whatsapp" className="rounded-none border-b-2 border-transparent px-0 py-3 shadow-none data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none">
            WhatsApp
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chats" className="mt-4">
      <section className="overflow-hidden rounded-xl border border-border bg-card">
        <form
          className="border-b border-border p-5"
          onSubmit={(event) => {
            event.preventDefault();
            handleApplyFilters();
          }}
        >
          <div className="grid items-center gap-3 xl:grid-cols-[minmax(250px,1.4fr)_minmax(200px,1fr)_minmax(150px,.65fr)_minmax(190px,.8fr)_auto]">
            <div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="search"
                  type="text"
                  className="pl-9"
                  aria-label={t('admin.chatLogs.filters.search', 'Conversation')}
                  placeholder={t(
                    'admin.chatLogs.filters.searchPlaceholder',
                    'Search conversation titles'
                  )}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="userSearch"
                  type="text"
                  className="pl-9"
                  aria-label={t('admin.chatLogs.filters.userSearch', 'User')}
                  placeholder={t(
                    'admin.chatLogs.filters.userSearchPlaceholder',
                    'Name or email'
                  )}
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                />
              </div>
            </div>

            <div>
              <Input
                id="tagSearch"
                type="text"
                placeholder={t('admin.chatLogs.filters.tagPlaceholder', 'Exact tag')}
                aria-label={t('admin.chatLogs.filters.tag', 'Tag')}
                value={tagSearch}
                onChange={(event) => setTagSearch(event.target.value)}
              />
            </div>

            <div>
              <Popover
                open={periodOpen}
                onOpenChange={(open) => {
                  setPeriodOpen(open);
                  if (open && dateFrom) {
                    const selected = new Date(`${dateFrom}T12:00:00`);
                    setVisibleMonth(
                      new Date(selected.getFullYear(), selected.getMonth(), 1)
                    );
                  }
                }}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-10 w-full justify-start gap-2 px-3 font-normal"
                  >
                    <CalendarRange className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate">{periodLabel}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-[340px] space-y-3 p-3">
                  <div className="grid grid-cols-4 gap-1 rounded-lg bg-muted/30 p-1">
                    <Button
                      type="button"
                      size="sm"
                      variant={!dateFrom && !dateTo ? 'secondary' : 'ghost'}
                      className="h-8 px-2 font-normal"
                      onClick={() => handleDatePreset()}
                    >
                      {t('admin.chatLogs.filters.all', 'All')}
                    </Button>
                    {DATE_PRESETS.map((days) => (
                      <Button
                        key={days}
                        type="button"
                        size="sm"
                        variant={activeDatePreset === days ? 'secondary' : 'ghost'}
                        className="h-8 px-2 font-normal"
                        onClick={() => handleDatePreset(days)}
                      >
                        {days}D
                      </Button>
                    ))}
                  </div>

                  <div className="rounded-lg bg-muted/30 p-3">
                    <div className="mb-3 flex items-center justify-between">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="fongpt-hover h-8 w-8"
                        onClick={() =>
                          setVisibleMonth(
                            (month) =>
                              new Date(month.getFullYear(), month.getMonth() - 1, 1)
                          )
                        }
                        aria-label={t(
                          'admin.chatLogs.filters.previousMonth',
                          'Previous month'
                        )}
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <p className="text-sm font-medium capitalize">
                        {visibleMonth.toLocaleDateString(calendarLocale, {
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="fongpt-hover h-8 w-8"
                        onClick={() =>
                          setVisibleMonth(
                            (month) =>
                              new Date(month.getFullYear(), month.getMonth() + 1, 1)
                          )
                        }
                        aria-label={t('admin.chatLogs.filters.nextMonth', 'Next month')}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>

                    <div className="mb-1 grid grid-cols-7 text-center text-[11px] text-muted-foreground">
                      {weekdays.map((weekday, index) => (
                        <span key={`${weekday}-${index}`} className="py-1">
                          {weekday}
                        </span>
                      ))}
                    </div>
                    <div className="grid grid-cols-7 gap-0.5">
                      {calendarDays(visibleMonth).map((date, index) => {
                        if (!date) return <span key={`empty-${index}`} />;
                        const value = dateInputValue(date);
                        const isEndpoint = value === dateFrom || value === dateTo;
                        const isInRange = Boolean(
                          dateFrom && dateTo && value > dateFrom && value < dateTo
                        );
                        const isToday = value === dateInputValue(new Date());
                        return (
                          <button
                            key={value}
                            type="button"
                            className={cn(
                              'grid h-9 place-items-center rounded-md text-sm transition-colors',
                              isEndpoint
                                ? 'bg-primary font-medium text-primary-foreground'
                                : isInRange
                                  ? 'bg-muted text-foreground'
                                  : 'fongpt-hover text-muted-foreground',
                              isToday && !isEndpoint && 'ring-1 ring-inset ring-border'
                            )}
                            onClick={() => handleCalendarDate(date)}
                            aria-label={date.toLocaleDateString(calendarLocale, {
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric'
                            })}
                          >
                            {date.getDate()}
                          </button>
                        );
                      })}
                    </div>
                    <p className="mt-3 text-center text-xs text-muted-foreground">
                      {!dateFrom || dateTo
                        ? t('admin.chatLogs.filters.chooseStart', 'Choose a start date')
                        : t('admin.chatLogs.filters.chooseEnd', 'Choose an end date')}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      className="mt-3 h-8 w-full"
                      onClick={handleApplyFilters}
                      disabled={!dateFrom || !dateTo}
                    >
                      {t('admin.chatLogs.filters.applyRange', 'Apply range')}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            <div className="flex items-center gap-2">
              <Button type="submit" className="h-10 px-5">
                {t('admin.chatLogs.filters.apply', 'Apply')}
              </Button>
              {hasActiveFilters ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-muted-foreground"
                  onClick={handleClearFilters}
                  title={t('admin.chatLogs.filters.clear', 'Clear filters')}
                  aria-label={t('admin.chatLogs.filters.clear', 'Clear filters')}
                >
                  <X className="h-4 w-4" />
                </Button>
              ) : null}
            </div>
          </div>
        </form>

        <div className="overflow-x-auto px-5 pb-2 pt-3">
          {loading ? (
            <div className="space-y-2 py-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-14 w-full" />
              ))}
            </div>
          ) : loadError ? (
            <div className="flex min-h-64 flex-col items-center justify-center text-center">
              <p className="font-medium">
                {t('admin.chatLogs.loadError', 'Could not load conversations')}
              </p>
              <Button className="mt-3" variant="outline" size="sm" onClick={fetchThreads}>
                {t('admin.chatLogs.retry', 'Try again')}
              </Button>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[38%]">
                      {t('admin.chatLogs.allConversations.columns.threadName', 'Conversation')}
                    </TableHead>
                    <TableHead className="w-[25%]">
                      {t('admin.chatLogs.allConversations.columns.user', 'User')}
                    </TableHead>
                    <TableHead>
                      {t('admin.chatLogs.allConversations.columns.createdAt', 'Created')}
                    </TableHead>
                    <TableHead>
                      {t('admin.chatLogs.allConversations.columns.tags', 'Tags')}
                    </TableHead>
                    <TableHead className="text-right">
                      {t('admin.chatLogs.allConversations.columns.actions', 'Actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {threads.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="h-64 text-center">
                        <Inbox className="mx-auto mb-3 h-8 w-8 text-muted-foreground/60" />
                        <p className="font-medium">
                          {t('admin.chatLogs.allConversations.noThreads', 'No conversations found')}
                        </p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {hasActiveFilters
                            ? t(
                                'admin.chatLogs.allConversations.adjustFilters',
                                'Try adjusting or clearing the filters.'
                              )
                            : t(
                                'admin.chatLogs.allConversations.empty',
                                'Conversations will appear here.'
                              )}
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    threads.map((thread) => {
                      const created = formatDate(thread.createdAt);
                      const user = thread.userName || thread.userIdentifier;
                      return (
                        <TableRow
                          key={thread.id}
                          className="fongpt-hover cursor-pointer"
                          onClick={() => viewThreadDetails(thread.id)}
                        >
                          <TableCell className="py-4 align-middle font-medium">
                            <div
                              className="max-w-[460px] truncate"
                              title={thread.name || undefined}
                            >
                              {thread.name ||
                                t('admin.chatLogs.allConversations.untitled', 'Untitled')}
                            </div>
                          </TableCell>
                          <TableCell className="py-4 align-middle">
                            <span className={user ? 'text-foreground' : 'text-muted-foreground'}>
                              {user ||
                                t('admin.chatLogs.allConversations.anonymous', 'Anonymous')}
                            </span>
                          </TableCell>
                          <TableCell className="whitespace-nowrap py-4 align-middle">
                            <span className="block text-sm">{created.date}</span>
                            <span className="block text-xs text-muted-foreground">
                              {created.time}
                            </span>
                          </TableCell>
                          <TableCell className="py-4 align-middle">
                            {thread.tags?.length > 0 ? (
                              <div className="flex flex-wrap gap-1.5">
                                {thread.tags.slice(0, 2).map((tag) => (
                                  <span
                                    key={tag}
                                    className="rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs text-muted-foreground"
                                  >
                                    {tag}
                                  </span>
                                ))}
                                {thread.tags.length > 2 ? (
                                  <span className="px-1 py-0.5 text-xs text-muted-foreground">
                                    +{thread.tags.length - 2}
                                  </span>
                                ) : null}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </TableCell>
                          <TableCell
                            className="py-4 text-right align-middle"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <div className="inline-flex items-center gap-1">
                              <Link
                                to={`/admin/chat-logs/${thread.id}?returnPage=${currentPage}`}
                                className={buttonVariants({
                                  variant: 'ghost',
                                  size: 'icon',
                                  className: 'h-8 w-8'
                                })}
                                title={t('admin.chatLogs.allConversations.viewButton', 'Open')}
                                aria-label={t('admin.chatLogs.allConversations.viewButton', 'Open')}
                              >
                                <ArrowUpRight className="h-4 w-4" />
                              </Link>
                              <Button
                                type="button"
                                size="icon"
                                variant="destructive"
                                onClick={(event) => handleDeleteClick(thread, event)}
                                className="h-8 w-8"
                                title={t('admin.chatLogs.allConversations.deleteButton', 'Delete')}
                                aria-label={t('admin.chatLogs.allConversations.deleteButton', 'Delete')}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>

              {totalThreads > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border py-4">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span>
                      {t('admin.chatLogs.pagination.showing', 'Showing')} {Math.min((currentPage - 1) * threadsPerPage + 1, totalThreads)} {t('admin.chatLogs.pagination.to', 'to')}{' '}
                      {Math.min(currentPage * threadsPerPage, totalThreads)} {t('admin.chatLogs.pagination.of', 'of')} {totalThreads} {t('admin.chatLogs.pagination.threads', 'threads')}
                    </span>
                    <Select
                      value={String(threadsPerPage)}
                      onValueChange={handlePageSizeChange}
                    >
                      <SelectTrigger
                        className="h-8 w-[72px]"
                        aria-label={t('admin.chatLogs.filters.rows', 'Rows')}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {PAGE_SIZES.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      aria-label={t('admin.chatLogs.pagination.previous', 'Previous')}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <div className="min-w-24 text-center text-sm tabular-nums text-muted-foreground">
                      {t('admin.chatLogs.pagination.page', 'Page')} {currentPage} {t('admin.chatLogs.pagination.of', 'of')} {Math.ceil(totalThreads / threadsPerPage)}
                    </div>
                    <Button
                      size="icon"
                      variant="outline"
                      className="h-8 w-8"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage * threadsPerPage >= totalThreads}
                      aria-label={t('admin.chatLogs.pagination.next', 'Next')}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>
        </TabsContent>
        <TabsContent value="telegram" className="mt-4"><AdminChannelMessages channel="telegram" /></TabsContent>
        <TabsContent value="whatsapp" className="mt-4"><AdminChannelMessages channel="whatsapp" /></TabsContent>
      </Tabs>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.chatLogs.deleteDialog.title', 'Delete Conversation')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.chatLogs.deleteDialog.description', 'Are you sure you want to delete this conversation? This action cannot be undone.')}
            </AlertDialogDescription>
            {/* Sibling of the description, not a child: Radix renders the
                description as a <p>, and a <div> inside it is invalid nesting. */}
            {threadToDelete && (
              <div className="max-h-24 overflow-y-auto rounded-md bg-muted p-2 text-sm">
                <strong className="break-words">
                  {threadToDelete.name
                    ? previewThreadName(threadToDelete.name)
                    : t('admin.chatLogs.allConversations.untitled', 'Untitled')}
                </strong>
              </div>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>
              {t('admin.chatLogs.deleteDialog.cancel', 'Cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? t('admin.chatLogs.deleteDialog.deleting', 'Deleting...') : t('admin.chatLogs.deleteDialog.confirm', 'Delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminChatLogs;
